import { EventObject } from '@xstate/store-react';
import { AppRoute } from '../../../enums/appRoutes';
import { StepKey } from '../../../enums/stepKey';
import { ApplianceFormData } from '../../../models/form/appliance';
import { StoreContext } from '../../../models/store/context';
import { log } from '@domgen/shared/utils/log';
import { FormKey } from '../../../enums/formKey';
import { QuoteStatus } from '../../../enums/quoteStatus';
import { postQuote } from '../../../services/api/postQuote';
import { formatQuoteRequest } from '../../../utils/quote/formatQuoteRequest';
import { store } from '../../store';
import { getFormCategoryName } from '../../../utils/store/getFormCategoryName';
import { getFormApplianceName } from '../../../utils/store/getFormApplianceName';
import { StoreEvent } from '../../../enums/storeEvent';
import { QuoteResponse, RegOnlyResponse } from '../../../models/services/api/postQuote';
import { findApplianceFromResults } from '@domgen/shared/utils/findApplianceFromResults';

export const submitRegistration = <T extends EventObject = EventObject>(
  context: StoreContext,
  event: T & {
    data: {
      [StepKey.PERSONAL_DETAILS]: ApplianceFormData[StepKey.PERSONAL_DETAILS];
    };
  },
  enqueue: {
    effect: (effect: () => Promise<void>) => void;
    emit: { navigate: (payload: { path: AppRoute; replace?: boolean }) => void };
  },
): StoreContext => {
  log.debug({ message: StoreEvent.SUBMIT, data: event.data });
  const form = {
    ...context.form,
    ...event.data,
  };

  const matchedAppliance = findApplianceFromResults(
    form[StepKey.MODEL_SERIAL]?.[FormKey.MODEL_NUMBER] || '',
    context.applianceLookupResults || [],
  );

  log.debug({
    message: 'Appliance matched from lookup results',
    data: matchedAppliance,
  });

  enqueue.effect(async () => {
    try {
      const quoteRequest = formatQuoteRequest({ ...context.form, ...event.data });

      if (!quoteRequest) {
        store.send({ type: StoreEvent.QUOTE_FAILED });
        return;
      }
      const { data, statusCode } = await postQuote(quoteRequest);

      if (statusCode === 200) {
        store.send({ type: StoreEvent.SET_QUOTE, data: data as QuoteResponse });
      } else {
        store.send({ type: StoreEvent.NO_QUOTE_REG_ONLY, data: data as RegOnlyResponse });
      }
    } catch (error) {
      console.error('Failed to submit quote', error);
      store.send({ type: StoreEvent.QUOTE_FAILED });
    }
  });

  const updatedContext = {
    ...context,
    form,
    quote: { status: QuoteStatus.LOADING },
    confirmation: {
      applianceDetails: {
        applianceName: getFormApplianceName(context),
        categoryName: getFormCategoryName(context),
        serialNumber: form[StepKey.MODEL_SERIAL]?.[FormKey.SERIAL_NUMBER],
        modelNumber: form[StepKey.MODEL_SERIAL]?.[FormKey.MODEL_NUMBER],
        arrivalDate: form[StepKey.ARRIVAL_DATE]?.[FormKey.ARRIVAL_DATE],
        purchasePrice: form[StepKey.PURCHASE_PRICE]?.[FormKey.PURCHASE_PRICE],
        guarantee: matchedAppliance?.guarantee ?? 12,
      },
    },
  } satisfies StoreContext;

  return updatedContext;
};
